/**
 * Optimized Batch Processing Service for Payroll Calculations
 * 
 * Addresses the performance bottleneck where batch processing was limited
 * to a hardcoded 5-concurrent limit, implementing dynamic concurrency scaling
 * based on system performance and workload characteristics.
 */

import { Employee, PayrollCalculation } from '@/types';
import { PayrollInputData } from '../core/types';

export interface BatchProcessingOptions {
  /** Stop processing on first error */
  stopOnError?: boolean;
  /** Minimum concurrent operations */
  minConcurrent?: number;
  /** Maximum concurrent operations */
  maxConcurrent?: number;
  /** Initial concurrent operations */
  initialConcurrent?: number;
  /** Enable adaptive concurrency adjustment */
  adaptiveConcurrency?: boolean;
  /** Progress callback */
  onProgress?: (progress: BatchProgress) => void;
  /** Error callback */
  onError?: (error: BatchError) => void;
}

export interface BatchProgress {
  completed: number;
  total: number;
  successful: number;
  failed: number;
  currentConcurrency: number;
  averageProcessingTime: number;
  estimatedTimeRemaining: number;
}

export interface BatchError {
  employee: Employee;
  error: Error;
  retryCount: number;
}

export interface BatchResult<T> {
  successful: Array<{ employee: Employee; result: T; processingTime: number }>;
  failed: Array<{ employee: Employee; error: Error; retryCount: number }>;
  metadata: {
    totalProcessingTime: number;
    averageProcessingTime: number;
    peakConcurrency: number;
    totalRetries: number;
  };
}

export class OptimizedBatchProcessor {
  private static readonly DEFAULT_OPTIONS: Required<BatchProcessingOptions> = {
    stopOnError: false,
    minConcurrent: 2,
    maxConcurrent: 20,
    initialConcurrent: 8,
    adaptiveConcurrency: true,
    onProgress: () => {},
    onError: () => {}
  };

  private currentConcurrency: number;
  private processingTimes: number[] = [];
  private errorCounts = new Map<string, number>();
  private readonly options: Required<BatchProcessingOptions>;

  constructor(options: BatchProcessingOptions = {}) {
    this.options = { ...OptimizedBatchProcessor.DEFAULT_OPTIONS, ...options };
    this.currentConcurrency = this.options.initialConcurrent;
  }

  /**
   * Process a batch of items with optimized concurrency control
   */
  async processBatch<T>(
    items: Employee[],
    processor: (employee: Employee, inputData: PayrollInputData) => Promise<T>,
    inputDataMap: Record<string, PayrollInputData>
  ): Promise<BatchResult<T>> {
    const startTime = Date.now();
    const successful: Array<{ employee: Employee; result: T; processingTime: number }> = [];
    const failed: Array<{ employee: Employee; error: Error; retryCount: number }> = [];
    
    let completed = 0;
    let peakConcurrency = this.currentConcurrency;
    let totalRetries = 0;

    // Create work queue
    const workQueue = items.map(employee => ({
      employee,
      inputData: inputDataMap[employee.id],
      retryCount: 0
    }));

    // Process items with dynamic concurrency
    while (workQueue.length > 0 || completed < items.length) {
      // Take up to currentConcurrency items from queue
      const currentBatch = workQueue.splice(0, this.currentConcurrency);
      
      if (currentBatch.length === 0) {
        // Wait for ongoing operations to complete
        await new Promise(resolve => setTimeout(resolve, 100));
        continue;
      }

      const batchStartTime = Date.now();
      const promises = currentBatch.map(async (item) => {
        const itemStartTime = Date.now();
        
        try {
          if (!item.inputData) {
            throw new Error(`No input data provided for employee ${item.employee.id}`);
          }

          const result = await processor(item.employee, item.inputData);
          const processingTime = Date.now() - itemStartTime;
          
          successful.push({ 
            employee: item.employee, 
            result, 
            processingTime 
          });
          
          this.processingTimes.push(processingTime);
          
          // Keep only last 50 processing times for moving average
          if (this.processingTimes.length > 50) {
            this.processingTimes.shift();
          }
          
          return { success: true, employee: item.employee, processingTime };
        } catch (error) {
          const errorObj = error instanceof Error ? error : new Error('Unknown error');
          
          // Track error for this employee
          const errorKey = item.employee.id;
          this.errorCounts.set(errorKey, (this.errorCounts.get(errorKey) || 0) + 1);
          
          // Retry logic
          if (item.retryCount < 2 && !this.options.stopOnError) {
            item.retryCount++;
            totalRetries++;
            workQueue.unshift(item); // Add back to front of queue for retry
            this.options.onError({ employee: item.employee, error: errorObj, retryCount: item.retryCount });
            return { success: false, employee: item.employee, error: errorObj, retry: true };
          } else {
            failed.push({ 
              employee: item.employee, 
              error: errorObj, 
              retryCount: item.retryCount 
            });
            this.options.onError({ employee: item.employee, error: errorObj, retryCount: item.retryCount });
            return { success: false, employee: item.employee, error: errorObj, retry: false };
          }
        }
      });

      const results = await Promise.all(promises);
      completed += results.filter(r => r.success || !r.retry).length;
      
      // Adaptive concurrency adjustment
      if (this.options.adaptiveConcurrency) {
        this.adjustConcurrency(Date.now() - batchStartTime, currentBatch.length);
        peakConcurrency = Math.max(peakConcurrency, this.currentConcurrency);
      }

      // Report progress
      const averageProcessingTime = this.processingTimes.length > 0 
        ? this.processingTimes.reduce((a, b) => a + b, 0) / this.processingTimes.length 
        : 0;
      
      const remaining = items.length - completed;
      const estimatedTimeRemaining = remaining > 0 
        ? (remaining / this.currentConcurrency) * averageProcessingTime 
        : 0;

      this.options.onProgress({
        completed,
        total: items.length,
        successful: successful.length,
        failed: failed.length,
        currentConcurrency: this.currentConcurrency,
        averageProcessingTime,
        estimatedTimeRemaining
      });

      if (this.options.stopOnError && failed.length > 0) {
        break;
      }
    }

    const totalProcessingTime = Date.now() - startTime;
    const averageProcessingTime = this.processingTimes.length > 0 
      ? this.processingTimes.reduce((a, b) => a + b, 0) / this.processingTimes.length 
      : 0;

    return {
      successful,
      failed,
      metadata: {
        totalProcessingTime,
        averageProcessingTime,
        peakConcurrency,
        totalRetries
      }
    };
  }

  /**
   * Adjust concurrency based on performance metrics
   */
  private adjustConcurrency(batchTime: number, batchSize: number): void {
    if (this.processingTimes.length < 5) {
      return; // Need more data points
    }

    const averageTime = this.processingTimes.reduce((a, b) => a + b, 0) / this.processingTimes.length;
    const recentTimes = this.processingTimes.slice(-5);
    const recentAverage = recentTimes.reduce((a, b) => a + b, 0) / recentTimes.length;

    // If recent performance is getting worse, reduce concurrency
    if (recentAverage > averageTime * 1.2 && this.currentConcurrency > this.options.minConcurrent) {
      this.currentConcurrency = Math.max(
        this.options.minConcurrent,
        Math.floor(this.currentConcurrency * 0.8)
      );
    }
    // If performance is good and stable, increase concurrency
    else if (recentAverage < averageTime * 0.9 && this.currentConcurrency < this.options.maxConcurrent) {
      this.currentConcurrency = Math.min(
        this.options.maxConcurrent,
        Math.floor(this.currentConcurrency * 1.2)
      );
    }
  }

  /**
   * Get current performance metrics
   */
  getPerformanceMetrics(): {
    currentConcurrency: number;
    averageProcessingTime: number;
    errorRate: number;
    totalProcessedItems: number;
  } {
    const totalErrors = Array.from(this.errorCounts.values()).reduce((a, b) => a + b, 0);
    const totalProcessed = this.processingTimes.length + totalErrors;
    
    return {
      currentConcurrency: this.currentConcurrency,
      averageProcessingTime: this.processingTimes.length > 0 
        ? this.processingTimes.reduce((a, b) => a + b, 0) / this.processingTimes.length 
        : 0,
      errorRate: totalProcessed > 0 ? totalErrors / totalProcessed : 0,
      totalProcessedItems: totalProcessed
    };
  }

  /**
   * Reset performance tracking
   */
  reset(): void {
    this.processingTimes = [];
    this.errorCounts.clear();
    this.currentConcurrency = this.options.initialConcurrent;
  }

  /**
   * Create a progress tracking callback that logs to console
   */
  static createConsoleProgressCallback(): (progress: BatchProgress) => void {
    return (progress: BatchProgress) => {
      const percentage = ((progress.completed / progress.total) * 100).toFixed(1);
      const eta = progress.estimatedTimeRemaining > 0 
        ? `ETA: ${Math.ceil(progress.estimatedTimeRemaining / 1000)}s` 
        : '';
      
      console.log(
        `Batch Progress: ${percentage}% (${progress.completed}/${progress.total}) ` +
        `Success: ${progress.successful}, Failed: ${progress.failed} ` +
        `Concurrency: ${progress.currentConcurrency} ${eta}`
      );
    };
  }

  /**
   * Create optimal batch processor based on system capabilities
   */
  static createOptimalProcessor(): OptimizedBatchProcessor {
    // Detect system capabilities (rough heuristic)
    const cores = typeof navigator !== 'undefined' && navigator.hardwareConcurrency 
      ? navigator.hardwareConcurrency 
      : 4;
    
    const maxConcurrent = Math.min(Math.max(cores * 2, 8), 50);
    const initialConcurrent = Math.max(Math.floor(cores * 1.5), 4);
    
    return new OptimizedBatchProcessor({
      minConcurrent: 2,
      maxConcurrent,
      initialConcurrent,
      adaptiveConcurrency: true,
      onProgress: OptimizedBatchProcessor.createConsoleProgressCallback(),
      onError: (error) => {
        console.warn(`Batch processing error for employee ${error.employee.id}:`, error.error.message);
      }
    });
  }
}